import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  CheckCircle,
  XCircle,
  ArrowClockwise,
  DownloadSimple,
  MagnifyingGlass,
  Spinner,
  Eye,
  Export,
  X,
  Printer,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvoiceHistory() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exactAmount, setExactAmount] = useState('');
  const [resending, setResending] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewNumber, setPreviewNumber] = useState('');
  const [showExport, setShowExport] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');

  const fetchInvoices = useCallback(async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(`${API}/api/invoices${params}`, {
        headers: getAuthHeaders(),
      });
      setInvoices(response.data);
    } catch {
      toast.error('Erreur chargement factures');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const filteredInvoices = invoices.filter((inv) => {
    if (dateFrom || dateTo) {
      const d = inv.created_at ? new Date(inv.created_at) : null;
      if (!d) return false;
      if (dateFrom && d < new Date(dateFrom + 'T00:00:00')) return false;
      if (dateTo && d > new Date(dateTo + 'T23:59:59')) return false;
    }
    if (exactAmount) {
      const target = parseFloat(exactAmount);
      if (!Number.isNaN(target)) {
        if (Math.abs((inv.price || 0) - target) >= 0.01) return false;
      }
    }
    return true;
  });

  const resetFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setExactAmount('');
  };

  const downloadPdf = async (number) => {
    try {
      const res = await axios.get(`${API}/api/invoices/${number}/pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur téléchargement');
    }
  };

  const preview = async (number) => {
    try {
      const res = await axios.get(`${API}/api/invoices/${number}/pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      setPreviewNumber(number);
    } catch {
      toast.error('Erreur chargement aperçu');
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewNumber('');
  };

  const resend = async (number) => {
    setResending((p) => ({ ...p, [number]: true }));
    try {
      const res = await axios.post(
        `${API}/api/invoices/${number}/resend`,
        {},
        { headers: getAuthHeaders() }
      );
      if (res.data.sent) toast.success('Facture renvoyée');
      else toast.error("Échec de l'envoi");
      fetchInvoices();
    } catch {
      toast.error('Erreur lors du renvoi');
    } finally {
      setResending((p) => ({ ...p, [number]: false }));
    }
  };

  const doExport = async (format) => {
    try {
      const qs = new URLSearchParams({ format });
      if (exportFrom) qs.set('date_from', exportFrom);
      if (exportTo) qs.set('date_to', exportTo);
      const res = await axios.get(`${API}/api/export/invoices?${qs}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const ext = format === 'xlsx' ? 'xlsx' : 'pdf';
      const mime =
        format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `factures_${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      setShowExport(false);
    } catch {
      toast.error('Erreur export');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Historique des factures</h2>
        {isAdmin && (
          <Button
            variant="outline"
            onClick={() => setShowExport(true)}
            className="gap-2 border-reco-500 text-reco-600"
            data-testid="invoice-export-btn"
          >
            <Export size={16} />
            Exporter
          </Button>
        )}
      </div>

      {/* Filter bar — single horizontal row */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par client ou N° facture..."
            className="pl-8 h-9 bg-white"
            data-testid="invoice-search-input"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-gray-500 whitespace-nowrap">Du</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[140px] bg-white" data-testid="invoice-date-from" />
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-gray-500 whitespace-nowrap">Au</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[140px] bg-white" data-testid="invoice-date-to" />
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-gray-500 whitespace-nowrap">Montant</Label>
          <Input type="number" step="0.01" value={exactAmount} onChange={(e) => setExactAmount(e.target.value)} placeholder="Ex: 300" className="h-9 w-[90px] bg-white" data-testid="invoice-amount-filter" />
        </div>
        {(search || dateFrom || dateTo || exactAmount) && (
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs text-reco-600 hover:text-reco-700 underline ml-auto whitespace-nowrap"
            data-testid="invoice-reset-filters"
          >
            Réinitialiser
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size={32} className="animate-spin text-reco-600" />
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          Aucune facture trouvée
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {filteredInvoices.map((inv, i) => (
            <div
              key={inv.number}
              className={`p-4 flex flex-col md:flex-row md:items-center gap-3 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
              data-testid={`invoice-row-${inv.number}`}
            >
              <div className="flex-shrink-0">
                <FileText size={20} className="text-reco-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                  {inv.number}
                  {(() => {
                    const t = inv.is_manual ? (inv.manual_invoice_type || 'manuelle') : 'auto';
                    const label = {
                      crevaison: 'Crevaison',
                      montage: 'Montage + déplacement',
                      libre: 'Ajout libre',
                      manuelle: 'Manuelle',
                      auto: 'Génération automatique',
                    }[t] || t;
                    const cls = {
                      crevaison: 'bg-red-100 text-red-700',
                      montage: 'bg-indigo-100 text-indigo-700',
                      libre: 'bg-purple-100 text-purple-700',
                      manuelle: 'bg-gray-100 text-gray-700',
                      auto: 'bg-green-100 text-green-700',
                    }[t] || 'bg-gray-100 text-gray-700';
                    return (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${cls}`} data-testid={`invoice-type-${inv.number}`}>
                        {label}
                      </span>
                    );
                  })()}
                </div>
                <div className="text-sm text-gray-500 truncate">
                  {inv.billing_name} • {new Date(inv.created_at).toLocaleDateString('fr-FR')}
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-gray-900">{inv.price?.toFixed(2)} €</div>
                <div className="flex items-center gap-1 text-xs mt-0.5">
                  {inv.status === 'sent' ? (
                    <span className="text-green-600 flex items-center gap-1"><CheckCircle size={12} />Envoyée</span>
                  ) : (
                    <span className="text-amber-600 flex items-center gap-1"><XCircle size={12} />Non envoyée</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => preview(inv.number)} title="Aperçu" data-testid={`invoice-preview-${inv.number}`}>
                  <Eye size={14} />
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPdf(inv.number)} title="Télécharger">
                  <DownloadSimple size={14} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={resending[inv.number]}
                  onClick={() => resend(inv.number)}
                  title="Renvoyer par email"
                >
                  {resending[inv.number] ? <Spinner size={14} className="animate-spin" /> : <ArrowClockwise size={14} />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview modal - custom full-screen overlay */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70"
          onClick={closePreview}
          data-testid="invoice-preview-overlay"
        >
          <div
            className="bg-white rounded-md shadow-2xl flex flex-col overflow-hidden"
            style={{ width: '95vw', height: '95vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Slim top bar */}
            <div
              className="flex items-center justify-between px-4 bg-gray-900 text-white border-b border-gray-800 flex-shrink-0"
              style={{ height: '40px' }}
            >
              <span className="text-sm font-medium truncate" data-testid="invoice-preview-title">
                {previewNumber}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => downloadPdf(previewNumber)}
                  className="p-1.5 hover:bg-white/10 rounded transition-colors"
                  title="Télécharger"
                  data-testid="invoice-preview-download"
                >
                  <DownloadSimple size={16} />
                </button>
                <button
                  onClick={() => {
                    const w = window.open(previewUrl, '_blank');
                    if (w) w.onload = () => w.print();
                  }}
                  className="p-1.5 hover:bg-white/10 rounded transition-colors"
                  title="Imprimer"
                  data-testid="invoice-preview-print"
                >
                  <Printer size={16} />
                </button>
                <button
                  onClick={closePreview}
                  className="p-1.5 hover:bg-white/10 rounded transition-colors ml-1"
                  title="Fermer"
                  data-testid="invoice-preview-close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            {/* PDF iframe fills remaining space */}
            <iframe
              src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              title="Aperçu facture"
              className="flex-1 w-full border-0 bg-gray-100"
              data-testid="invoice-preview-iframe"
            />
          </div>
        </div>
      )}

      {/* Export modal */}
      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Exporter les factures</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Du</Label>
                <Input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} data-testid="invoice-export-from" />
              </div>
              <div>
                <Label>Au</Label>
                <Input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} data-testid="invoice-export-to" />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => doExport('xlsx')} className="flex-1" data-testid="invoice-export-xlsx">
                Excel (.xlsx)
              </Button>
              <Button onClick={() => doExport('pdf')} variant="outline" className="flex-1" data-testid="invoice-export-pdf">
                PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
